'use client';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Divider,
  Paper,
  Menu,
  MenuItem,
  Button,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  ListItemText,
  OutlinedInput,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  Chip,
  Checkbox,
  ListSubheader
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid } from '@mui/x-data-grid';
import { zhCN, idID } from '@mui/x-data-grid/locales';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import SelectionMultiDialog from './SelectionMultiDialog';
import { useLanguage } from '../utils/i18n';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 320
    }
  }
};

const DataTable = ({
  title = '',
  rows = [],
  columns = [],
  columnGroupingModel = [],
  loading = false,
  pageSizeOptions = [10, 25, 50, 100],
  paginationMode = 'server',
  rowCount,
  paginationModel,
  onPaginationModelChange,
  searchQuery = '',
  onSearchChange = () => {},
  onSearch = null,
  onAddClick = () => {},
  searchLabel = 'Search',
  companyOptions = [],
  selectedCompanies = [],
  onCompanyChange = () => {},
  onCompanySearch = null,
  plateOptions = [],
  selectedPlates = [],
  onPlateChange = () => {},

  // === Props untuk Vehicle Type ===
  vehicleTypeOptions = [],
  selectedVehicleTypes = [],
  onVehicleTypeChange = () => {},
  // === Props untuk Day Inspected ===
  inspectedOptions = [],
  selectedInspected = [],
  onInspectedChange = () => {},
  // === Props untuk Day Not Inspected ===
  notInspectedOptions = [],
  selectedNotInspected = [],
  onNotInspectedChange = () => {},
  // ===

  hideAddButton = false,
  hideCompanyFilter = false,
  hidePlateFilter = false,

  // === Props untuk Menyembunyikan ===
  hideVehicleTypeFilter = false,
  hideInspectedFilter = false,
  hideNotInspectedFilter = false,
  hideSearchField = false,
  // ===
  statusOptions = [],
  showStatusFilter = false,
  initialStatus = '',
  onStatusChange = null,

  searchFieldOptions = null,
  selectedSearchField = null,
  onSearchFieldChange = null,
  showDateFilter = false,
  initialFromDate = '',
  initialToDate = '',
  onDateRangeChange = null,
  hideExportButton = false,
  extraButtons = null,
  cursorPointer = false,

  onExportExcel,
  onExportCSV,
  onExportPDF,
  onFetchAllForExport,

  onCellClick,
  headerHeight = 65,
  rowHeight = 'auto',
  localeText
}) => {
  const { language, t } = useLanguage();
  const [companySearchTerm, setCompanySearchTerm] = useState('');

  const filteredCompanyOptions = useMemo(() => {
    if (!companySearchTerm || typeof onCompanySearch === 'function') return companyOptions || [];
    const term = companySearchTerm.toLowerCase();
    return (companyOptions || []).filter((opt) => {
      const label = (opt.label ?? opt.name ?? String(opt.value)).toLowerCase();
      return label.includes(term);
    });
  }, [companyOptions, companySearchTerm, onCompanySearch]);

  const activeLocaleText = useMemo(() => {
    if (localeText) return localeText;
    if (language === 'zh') return zhCN.components.MuiDataGrid.defaultProps.localeText;
    if (language === 'id') return idID.components.MuiDataGrid.defaultProps.localeText;
    return undefined;
  }, [language, localeText]);

  const [statusValue, setStatusValue] = useState(initialStatus ?? '');
  useEffect(() => {
    if (initialStatus != null) setStatusValue(initialStatus);
  }, [initialStatus]);
  const handleStatusChange = (e) => {
    const v = e?.target?.value ?? '';
    setStatusValue(v);
    if (typeof onStatusChange === 'function') {
      onStatusChange(v);
      return;
    }
    if (typeof onSearch === 'function') {
      onSearch({ field: 'status', value: v });
      return;
    }
    if (typeof onSearchChange === 'function') {
      onSearchChange({ target: { value: v } });
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const tableRef = useRef(null);

  const [fromDate, setFromDate] = useState(initialFromDate || '');
  const [toDate, setToDate] = useState(initialToDate || '');

  const propSyncRef = useRef(false);
  const lastDatePayloadRef = useRef(null);

  useEffect(() => {
    propSyncRef.current = true;
    setFromDate(initialFromDate || '');
  }, [initialFromDate]);

  useEffect(() => {
    propSyncRef.current = true;
    setToDate(initialToDate || '');
  }, [initialToDate]);

  useEffect(() => {
    if (propSyncRef.current) {
      propSyncRef.current = false;
      lastDatePayloadRef.current = { from: fromDate || null, to: toDate || null };
      return;
    }
    const payload = { from: fromDate || null, to: toDate || null };
    const last = lastDatePayloadRef.current;
    if (last && last.from === payload.from && last.to === payload.to) return;
    lastDatePayloadRef.current = payload;
    if (typeof onDateRangeChange === 'function') {
      try {
        onDateRangeChange(payload);
      } catch (e) {
        console.error('[DataTable] onDateRangeChange error', e);
      }
    }
  }, [fromDate, toDate, onDateRangeChange]);

  const [internalSearchField, setInternalSearchField] = useState(
    selectedSearchField ?? (Array.isArray(searchFieldOptions) && searchFieldOptions.length ? searchFieldOptions[0].value : null)
  );
  useEffect(() => {
    if (selectedSearchField != null) setInternalSearchField(selectedSearchField);
  }, [selectedSearchField]);

  const dynamicColumns = useMemo(() => {
    if ((!columns || columns.length === 0) && rows && rows.length > 0) {
      const sample = rows[0];
      const autoCols = Object.keys(sample).map((key) => ({
        field: key,
        headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || key,
        flex: 1,
        renderCell: (params) => params.value ?? '-'
      }));
      return [
        {
          field: '__no__',
          headerName: t('dataTable.no', 'No'),
          width: 10,
          sortable: false,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const index = params.api.getRowIndexRelativeToVisibleRows(params.id) + 1;
            if (paginationModel && paginationMode === 'server') {
              return paginationModel.page * paginationModel.pageSize + index;
            }
            return index;
          }
        },
        ...autoCols
      ];
    }

    if (columns && columns.length > 0) {
      return [
        {
          field: '__no__',
          headerName: t('dataTable.no', 'No'),
          width: 10,
          sortable: false,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const index = params.api.getRowIndexRelativeToVisibleRows(params.id) + 1;
            if (paginationModel && paginationMode === 'server') {
              return paginationModel.page * paginationModel.pageSize + index;
            }
            return index;
          }
        },
        ...columns
      ];
    }

    return [];
  }, [columns, rows, t, paginationModel, paginationMode]);

  // =========================
  // // Fungsi - Helpers
  // =========================
  const getCellValue = (row, col, index) => {
    try {
      if (col.field === '__no__') return index + 1;

      if (typeof col.valueGetter === 'function') {
        // Cek jumlah parameter yang diminta fungsi valueGetter.
        // DataGrid v6+ menggunakan signature (value, row, column, apiRef) -> arity >= 2
        // DataGrid v5 menggunakan signature (params) -> arity = 1
        if (col.valueGetter.length > 1) {
          return col.valueGetter(row[col.field], row, col, null) ?? '';
        } else {
          return col.valueGetter({ row, value: row[col.field], colDef: col, id: index }) ?? '';
        }
      }

      // Fallback khusus untuk kolom Company yang sering tidak pakai valueGetter
      if (col.field === 'company' || col.field === 'company_id') {
        if (row.join && row.join.company_name) return row.join.company_name;
        if (row.company_name) return row.company_name;
      }

      return row[col.field] ?? '';
    } catch {
      return row[col.field] ?? '';
    }
  };

  const getSelectedValuesFromProp = (sel) => {
    if (!sel) return [];
    if (!Array.isArray(sel)) return [];
    if (sel.length > 0 && typeof sel[0] === 'object' && (sel[0].value !== undefined || sel[0].id !== undefined)) {
      return sel.map((s) => String(s.value ?? s.id ?? s));
    }
    return sel.map((s) => String(s));
  };

  // =========================
  // // Fungsi - Export
  // =========================
  const getFormattedDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const [isExporting, setIsExporting] = useState(false);
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      if (onExportCSV) {
        const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
        await onExportCSV(dataToExport);
        return;
      }
      // Jika parent mengirimkan fungsi ambil semua data, jalankan. Jika tidak, gunakan 'rows' saat ini.
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;

      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);

      const formattedData = dataToExport.map((row, index) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          let val = getCellValue(row, col, index);
          if (Array.isArray(val)) val = val.join(', ');
          rowValues[col.headerName || col.field] = val;
        });
        return rowValues;
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      if (onExportExcel) {
        const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
        await onExportExcel(dataToExport);
        return;
      }
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;

      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(title || 'Sheet1');
      worksheet.columns = exportCols.map((col) => ({
        header: col.headerName || col.field,
        key: col.field,
        width: col.width ? Math.min(Math.max(col.width / 8, 10), 60) : 30
      }));

      let currentExcelRow = 2; // Row 1 is header
      dataToExport.forEach((row, index) => {
        const rowValues = {};
        let maxSubRows = 1;

        exportCols.forEach((col) => {
          const val = getCellValue(row, col, index);
          if (Array.isArray(val)) {
            if (val.length > maxSubRows) maxSubRows = val.length;
          }
          rowValues[col.field] = val;
        });

        for (let i = 0; i < maxSubRows; i++) {
          const subRowValues = {};
          exportCols.forEach((col) => {
            const val = rowValues[col.field];
            if (Array.isArray(val)) {
              subRowValues[col.field] = val[i] !== undefined ? val[i] : '';
            } else {
              subRowValues[col.field] = i === 0 ? val : '';
            }
          });
          worksheet.addRow(subRowValues);
        }

        if (maxSubRows > 1) {
          exportCols.forEach((col, colIdx) => {
            const val = rowValues[col.field];
            if (!Array.isArray(val)) {
              const colLetter = worksheet.getColumn(colIdx + 1).letter;
              worksheet.mergeCells(`${colLetter}${currentExcelRow}:${colLetter}${currentExcelRow + maxSubRows - 1}`);
              worksheet.getCell(`${colLetter}${currentExcelRow}`).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            }
          });
        }
        currentExcelRow += maxSubRows;
      });

      // Styling header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1976D2' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Menambahkan border
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      if (onExportPDF) {
        const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
        await onExportPDF(dataToExport);
        return;
      }
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;

      const doc = new jsPDF('landscape');

      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);
      const tableColumn = exportCols.map((col) => col.headerName || col.field);

      const tableRows = dataToExport.map((row, index) => {
        return exportCols.map((col) => {
          let val = getCellValue(row, col, index);
          if (Array.isArray(val)) val = val.join('\n');
          return val !== null && val !== undefined ? String(val) : '-';
        });
      });

      if (title) {
        doc.setFontSize(16);
        doc.text(title, 14, 15);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: title ? 20 : 14,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] } // MUI Primary Blue
      });

      doc.save(`${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };
  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  // =========================
  // // Fungsi - Select / Multi Select handlers
  // =========================
  const propSelectedCompanyValues = getSelectedValuesFromProp(selectedCompanies);
  const propSelectedPlateValues = getSelectedValuesFromProp(selectedPlates);
  const propSelectedVehicleTypeValues = getSelectedValuesFromProp(selectedVehicleTypes);
  const propSelectedInspectedValues = getSelectedValuesFromProp(selectedInspected);
  const propSelectedNotInspectedValues = getSelectedValuesFromProp(selectedNotInspected);

  const [localSelectedCompanies, setLocalSelectedCompanies] = useState(null);
  const [localSelectedPlates, setLocalSelectedPlates] = useState(null);
  const [localSelectedVehicleTypes, setLocalSelectedVehicleTypes] = useState(null);
  const [localSelectedInspected, setLocalSelectedInspected] = useState(null);
  const [localSelectedNotInspected, setLocalSelectedNotInspected] = useState(null);

  useEffect(() => {
    if (Array.isArray(selectedCompanies) && selectedCompanies.length > 0) {
      setLocalSelectedCompanies(null);
    }
  }, [selectedCompanies]);

  useEffect(() => {
    if (Array.isArray(selectedPlates) && selectedPlates.length > 0) {
      setLocalSelectedPlates(null);
    }
  }, [selectedPlates]);

  useEffect(() => {
    if (Array.isArray(selectedVehicleTypes) && selectedVehicleTypes.length > 0) {
      setLocalSelectedVehicleTypes(null);
    }
  }, [selectedVehicleTypes]);

  useEffect(() => {
    if (Array.isArray(selectedInspected) && selectedInspected.length > 0) {
      setLocalSelectedInspected(null);
    }
  }, [selectedInspected]);

  useEffect(() => {
    if (Array.isArray(selectedNotInspected) && selectedNotInspected.length > 0) {
      setLocalSelectedNotInspected(null);
    }
  }, [selectedNotInspected]);

  const selectedCompanyValues = localSelectedCompanies ? getSelectedValuesFromProp(localSelectedCompanies) : propSelectedCompanyValues;
  const selectedPlateValues = localSelectedPlates ? getSelectedValuesFromProp(localSelectedPlates) : propSelectedPlateValues;
  const selectedVehicleTypeValues = localSelectedVehicleTypes
    ? getSelectedValuesFromProp(localSelectedVehicleTypes)
    : propSelectedVehicleTypeValues;
  const selectedInspectedValues = localSelectedInspected ? getSelectedValuesFromProp(localSelectedInspected) : propSelectedInspectedValues;
  const selectedNotInspectedValues = localSelectedNotInspected
    ? getSelectedValuesFromProp(localSelectedNotInspected)
    : propSelectedNotInspectedValues;

  const handleMuiSelectChange = (event) => {
    let value = event.target.value || [];
    value = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
    if (value.includes('all')) {
      if (selectedCompanyValues.length === (companyOptions || []).length) {
        onCompanyChange([]);
        setLocalSelectedCompanies([]);
      } else {
        const all = (companyOptions || []).map((opt) => ({ value: opt.value, label: opt.label ?? opt.name ?? String(opt.value) }));
        onCompanyChange(all);
        setLocalSelectedCompanies(all);
      }
      return;
    }
    const selectedObjs = (companyOptions || [])
      .filter((opt) => value.includes(String(opt.value)))
      .map((opt) => ({ value: opt.value, label: opt.label ?? opt.name ?? String(opt.value) }));
    onCompanyChange(selectedObjs);
    setLocalSelectedCompanies(selectedObjs);
  };

  const handlePlateMuiSelectChange = (event) => {
    let value = event.target.value || [];
    value = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
    if (value.includes('all')) {
      if (selectedPlateValues.length === (plateOptions || []).length) {
        onPlateChange([]);
        setLocalSelectedPlates([]);
      } else {
        const all = (plateOptions || []).map((opt) => ({
          value: opt.value ?? opt.id ?? opt,
          label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt)
        }));
        onPlateChange(all);
        setLocalSelectedPlates(all);
      }
      return;
    }
    const selectedObjs = (plateOptions || [])
      .filter((opt) => value.includes(String(opt.value ?? opt.id ?? opt)))
      .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
    onPlateChange(selectedObjs);
    setLocalSelectedPlates(selectedObjs);
  };

  const handleVehicleTypeMuiSelectChange = (event) => {
    let value = event.target.value || [];
    value = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
    if (value.includes('all')) {
      if (selectedVehicleTypeValues.length === (vehicleTypeOptions || []).length) {
        onVehicleTypeChange([]);
        setLocalSelectedVehicleTypes([]);
      } else {
        const all = (vehicleTypeOptions || []).map((opt) => ({
          value: opt.value ?? opt.id ?? opt,
          label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt)
        }));
        onVehicleTypeChange(all);
        setLocalSelectedVehicleTypes(all);
      }
      return;
    }
    const selectedObjs = (vehicleTypeOptions || [])
      .filter((opt) => value.includes(String(opt.value ?? opt.id ?? opt)))
      .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
    onVehicleTypeChange(selectedObjs);
    setLocalSelectedVehicleTypes(selectedObjs);
  };

  const handleInspectedMuiSelectChange = (event) => {
    let value = event.target.value || [];
    value = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
    if (value.includes('all')) {
      if (selectedInspectedValues.length === (inspectedOptions || []).length) {
        onInspectedChange([]);
        setLocalSelectedInspected([]);
      } else {
        const all = (inspectedOptions || []).map((opt) => ({
          value: opt.value ?? opt.id ?? opt,
          label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt)
        }));
        onInspectedChange(all);
        setLocalSelectedInspected(all);
      }
      return;
    }
    const selectedObjs = (inspectedOptions || [])
      .filter((opt) => value.includes(String(opt.value ?? opt.id ?? opt)))
      .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
    onInspectedChange(selectedObjs);
    setLocalSelectedInspected(selectedObjs);
  };

  const handleNotInspectedMuiSelectChange = (event) => {
    let value = event.target.value || [];
    value = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)];
    if (value.includes('all')) {
      if (selectedNotInspectedValues.length === (notInspectedOptions || []).length) {
        onNotInspectedChange([]);
        setLocalSelectedNotInspected([]);
      } else {
        const all = (notInspectedOptions || []).map((opt) => ({
          value: opt.value ?? opt.id ?? opt,
          label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt)
        }));
        onNotInspectedChange(all);
        setLocalSelectedNotInspected(all);
      }
      return;
    }
    const selectedObjs = (notInspectedOptions || [])
      .filter((opt) => value.includes(String(opt.value ?? opt.id ?? opt)))
      .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
    onNotInspectedChange(selectedObjs);
    setLocalSelectedNotInspected(selectedObjs);
  };

  const allCompanyOption = { value: 'all', label: t('dataTable.selectAll', 'Select All') };
  const allPlateOption = { value: 'all', label: t('dataTable.selectAll', 'Select All') };
  const allVehicleTypeOption = { value: 'all', label: t('dataTable.selectAll', 'Select All') };
  const allInspectedOption = { value: 'all', label: t('dataTable.selectAll', 'Select All') };
  const allNotInspectedOption = { value: 'all', label: t('dataTable.selectAll', 'Select All') };

  const MAX_VISIBLE = 1;

  const renderCompanyValue = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const filtered = selectedArr.filter((v) => String(v) !== 'all');
    const visible = filtered.slice(0, MAX_VISIBLE);
    const overflow = Math.max(0, filtered.length - MAX_VISIBLE);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {visible.map((val) => {
          const found = (companyOptions || []).find((o) => String(o.value) === String(val));
          const label = found ? (found.label ?? found.name ?? String(found.value)) : String(val);
          return <Chip key={val} label={label} size="small" />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCompanyModalOpen(true);
            }}
            sx={{ ml: 0.5 }}
          >
            +{overflow} {t('more', 'more')}
          </Button>
        )}
        {filtered.length === 0 && <Typography variant="body2">—</Typography>}
      </Box>
    );
  };

  const renderPlateValue = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const filtered = selectedArr.filter((v) => String(v) !== 'all');
    const visible = filtered.slice(0, MAX_VISIBLE);
    const overflow = Math.max(0, filtered.length - MAX_VISIBLE);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {visible.map((val) => {
          const found = (plateOptions || []).find((o) => String(o.value ?? o.id ?? o) === String(val));
          const label = found ? (found.label ?? found.name ?? String(found.value ?? found.id ?? found)) : String(val);
          return <Chip key={val} label={label} size="small" />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setPlateModalOpen(true);
            }}
            sx={{ ml: 0.5 }}
          >
            +{overflow} {t('general.more', 'More')}
          </Button>
        )}
        {filtered.length === 0 && <Typography variant="body2">—</Typography>}
      </Box>
    );
  };

  const renderVehicleTypeValue = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const filtered = selectedArr.filter((v) => String(v) !== 'all');
    const visible = filtered.slice(0, MAX_VISIBLE);
    const overflow = Math.max(0, filtered.length - MAX_VISIBLE);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {visible.map((val) => {
          const found = (vehicleTypeOptions || []).find((o) => String(o.value ?? o.id ?? o) === String(val));
          const label = found ? (found.label ?? found.name ?? String(found.value ?? found.id ?? found)) : String(val);
          return <Chip key={val} label={label} size="small" />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setVehicleTypeModalOpen(true);
            }}
            sx={{ ml: 0.5 }}
          >
            +{overflow} {t('general.more', 'More')}
          </Button>
        )}
        {filtered.length === 0 && <Typography variant="body2">—</Typography>}
      </Box>
    );
  };

  const renderInspectedValue = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const filtered = selectedArr.filter((v) => String(v) !== 'all');
    const visible = filtered.slice(0, MAX_VISIBLE);
    const overflow = Math.max(0, filtered.length - MAX_VISIBLE);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {visible.map((val) => {
          const found = (inspectedOptions || []).find((o) => String(o.value ?? o.id ?? o) === String(val));
          const label = found ? (found.label ?? found.name ?? String(found.value ?? found.id ?? found)) : String(val);
          return <Chip key={val} label={label} size="small" />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setInspectedModalOpen(true);
            }}
            sx={{ ml: 0.5 }}
          >
            +{overflow} {t('general.more', 'More')}
          </Button>
        )}
        {filtered.length === 0 && <Typography variant="body2">—</Typography>}
      </Box>
    );
  };

  const renderNotInspectedValue = (selected) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const filtered = selectedArr.filter((v) => String(v) !== 'all');
    const visible = filtered.slice(0, MAX_VISIBLE);
    const overflow = Math.max(0, filtered.length - MAX_VISIBLE);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {visible.map((val) => {
          const found = (notInspectedOptions || []).find((o) => String(o.value ?? o.id ?? o) === String(val));
          const label = found ? (found.label ?? found.name ?? String(found.value ?? found.id ?? found)) : String(val);
          return <Chip key={val} label={label} size="small" />;
        })}
        {overflow > 0 && (
          <Button
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setNotInspectedModalOpen(true);
            }}
            sx={{ ml: 0.5 }}
          >
            +{overflow} {t('general.more', 'More')}
          </Button>
        )}
        {filtered.length === 0 && <Typography variant="body2">—</Typography>}
      </Box>
    );
  };

  // =========================
  // // Fungsi - Auto apply company from localStorage
  // =========================
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (typeof window === 'undefined') return;
    if (!companyOptions || companyOptions.length === 0) return;
    if (selectedCompanyValues && selectedCompanyValues.length > 0) return;

    let storedCompany = null;
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser);
          if (parsed) {
            storedCompany = parsed.company ?? parsed.company_id ?? null;
          }
        } catch (e) {
          console.log(e);
        }
      }
    } catch (e) {
      console.log(e);
    }

    if (storedCompany == null) {
      try {
        const rawCompany = localStorage.getItem('companyId');
        if (rawCompany) storedCompany = rawCompany;
      } catch (e) {
        console.log(e);
      }
    }

    if (!storedCompany && storedCompany !== 0) return;

    const stored = String(storedCompany);

    const match = companyOptions.find((o) => String(o.value ?? o.id ?? o) === stored || String(o.id ?? o.value ?? o) === stored);

    const obj = match
      ? { value: String(match.value ?? match.id ?? match), label: match.label ?? match.name ?? String(match.value ?? match.id ?? match) }
      : { value: String(stored), label: String(stored) };

    autoAppliedRef.current = true;
    try {
      onCompanyChange([obj]);
    } catch (e) {
      console.error('[DataTable] onCompanyChange auto apply error', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOptions, selectedCompanyValues]);

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [plateModalOpen, setPlateModalOpen] = useState(false);
  const [vehicleTypeModalOpen, setVehicleTypeModalOpen] = useState(false);
  const [inspectedModalOpen, setInspectedModalOpen] = useState(false);
  const [notInspectedModalOpen, setNotInspectedModalOpen] = useState(false);

  // =========================
  // // Fungsi - Search / Field selectors
  // =========================
  const currentFieldLabel =
    (Array.isArray(searchFieldOptions) && searchFieldOptions.find((s) => String(s.value) === String(internalSearchField))?.label) ||
    searchLabel ||
    t('general.search', 'Search');

  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const openFieldMenu = Boolean(fieldMenuAnchor);
  const handleFieldButtonClick = (e) => setFieldMenuAnchor(e.currentTarget);
  const handleFieldMenuClose = () => setFieldMenuAnchor(null);
  const handleFieldSelect = (val) => {
    handleFieldMenuClose();
    if (onSearchFieldChange) onSearchFieldChange(val);
    if (selectedSearchField == null) setInternalSearchField(val);
  };

  const handleSearchInputChange = (e) => {
    const val = e?.target?.value ?? '';
    const field = internalSearchField;
    if (typeof onSearch === 'function') {
      onSearch({ field, value: val, originalEvent: e });
      return;
    }
    if (typeof onSearchChange === 'function') {
      onSearchChange(e);
    }
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
  };

  // =========================
  // // Render
  // =========================
  const normalizedExtraButtons = React.Children.toArray(extraButtons);

  return (
    <Paper
      sx={{
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        p: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {!hideCompanyFilter && ((companyOptions && companyOptions.length > 0) || typeof onCompanySearch === 'function') && (
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="company-multi-label">{t('general.company', 'Company')}</InputLabel>
              <MuiSelect
                labelId="company-multi-label"
                multiple
                value={selectedCompanyValues}
                onChange={handleMuiSelectChange}
                input={<OutlinedInput id="select-multiple-chip" label={t('general.company', 'Company')} />}
                renderValue={renderCompanyValue}
                MenuProps={{
                  ...MenuProps,
                  autoFocus: false
                }}
              >
                <ListSubheader onKeyDown={(e) => e.stopPropagation()} sx={{ bgcolor: 'background.paper', p: 1, zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder={t('dataTable.searchCompany', 'Search company...')}
                    fullWidth
                    value={companySearchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanySearchTerm(val);
                      if (typeof onCompanySearch === 'function') {
                        onCompanySearch(val);
                      }
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </ListSubheader>
                {[allCompanyOption, ...(filteredCompanyOptions || [])].map((opt) => {
                  const val = String(opt.value);
                  const label = opt.label ?? opt.name ?? String(opt.value);
                  const checked =
                    val === 'all' ? selectedCompanyValues.length === (companyOptions || []).length : selectedCompanyValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </FormControl>
          )}
          <FormControl sx={{ minWidth: 200, display: showStatusFilter ? 'inline-flex' : 'none' }}>
            <InputLabel id="status-select-label">{t('fuelPrice.status', 'Status')}</InputLabel>
            <MuiSelect
              labelId="status-select-label"
              value={statusValue}
              label={t('fuelPrice.status', 'Status')}
              onChange={handleStatusChange}
              size="small"
              sx={{ minWidth: 180 }}
              MenuProps={MenuProps}
            >
              {(statusOptions || []).map((opt) => {
                const val = String(opt.value);
                const label = opt.label ?? String(opt.value);
                return (
                  <MenuItem key={val} value={val}>
                    <ListItemText primary={label} />
                  </MenuItem>
                );
              })}
            </MuiSelect>
          </FormControl>
          {!hidePlateFilter && plateOptions && plateOptions.length > 0 && (
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="plate-multi-label">{t('general.plate', 'Plate')}</InputLabel>
              <MuiSelect
                labelId="plate-multi-label"
                multiple
                value={selectedPlateValues}
                onChange={handlePlateMuiSelectChange}
                input={<OutlinedInput id="select-multiple-plate" label={t('general.plate', 'Plate')} />}
                renderValue={renderPlateValue}
                MenuProps={MenuProps}
              >
                {[allPlateOption, ...(plateOptions || [])].map((opt) => {
                  const val = String(opt.value ?? opt.id ?? opt);
                  const label = opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt);
                  const checked =
                    val === 'all' ? selectedPlateValues.length === (plateOptions || []).length : selectedPlateValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </FormControl>
          )}

          {!hideVehicleTypeFilter && vehicleTypeOptions && vehicleTypeOptions.length > 0 && (
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="vehicle-type-multi-label">{t('general.vehicleType', 'Vehicle Type')}</InputLabel>
              <MuiSelect
                labelId="vehicle-type-multi-label"
                multiple
                value={selectedVehicleTypeValues}
                onChange={handleVehicleTypeMuiSelectChange}
                input={<OutlinedInput id="select-multiple-vehicle-type" label={t('general.vehicleType', 'Vehicle Type')} />}
                renderValue={renderVehicleTypeValue}
                MenuProps={MenuProps}
              >
                {[allVehicleTypeOption, ...(vehicleTypeOptions || [])].map((opt) => {
                  const val = String(opt.value ?? opt.id ?? opt);
                  const label = opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt);
                  const checked =
                    val === 'all'
                      ? selectedVehicleTypeValues.length === (vehicleTypeOptions || []).length
                      : selectedVehicleTypeValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </FormControl>
          )}

          {!hideInspectedFilter && inspectedOptions && inspectedOptions.length > 0 && (
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="inspected-multi-label">{t('dataTable.dayInspected', 'Day Inspected')}</InputLabel>
              <MuiSelect
                labelId="inspected-multi-label"
                multiple
                value={selectedInspectedValues}
                onChange={handleInspectedMuiSelectChange}
                input={<OutlinedInput id="select-multiple-inspected" label={t('dataTable.dayInspected', 'Day Inspected')} />}
                renderValue={renderInspectedValue}
                MenuProps={MenuProps}
              >
                {[allInspectedOption, ...(inspectedOptions || [])].map((opt) => {
                  const val = String(opt.value ?? opt.id ?? opt);
                  const label = opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt);
                  const checked =
                    val === 'all'
                      ? selectedInspectedValues.length === (inspectedOptions || []).length
                      : selectedInspectedValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </FormControl>
          )}

          {!hideNotInspectedFilter && notInspectedOptions && notInspectedOptions.length > 0 && (
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="not-inspected-multi-label">{t('dataTable.dayNotInspected', 'Day Not Inspected')}</InputLabel>
              <MuiSelect
                labelId="not-inspected-multi-label"
                multiple
                value={selectedNotInspectedValues}
                onChange={handleNotInspectedMuiSelectChange}
                input={<OutlinedInput id="select-multiple-not-inspected" label={t('dataTable.dayNotInspected', 'Day Not Inspected')} />}
                renderValue={renderNotInspectedValue}
                MenuProps={MenuProps}
              >
                {[allNotInspectedOption, ...(notInspectedOptions || [])].map((opt) => {
                  const val = String(opt.value ?? opt.id ?? opt);
                  const label = opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt);
                  const checked =
                    val === 'all'
                      ? selectedNotInspectedValues.length === (notInspectedOptions || []).length
                      : selectedNotInspectedValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </MuiSelect>
            </FormControl>
          )}

          {showDateFilter && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 1 }}>
              <TextField
                label={t('general.from', 'From')}
                type="date"
                size="medium"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label={t('general.to', 'To')}
                type="date"
                size="medium"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button variant="text" startIcon={<RotateRightOutlinedIcon />} onClick={clearDateFilter}>
                {t('general.reset', 'Reset')}
              </Button>
            </Box>
          )}

          {!hideSearchField &&
            (Array.isArray(searchFieldOptions) && searchFieldOptions.length > 0 ? (
              <TextField
                label={currentFieldLabel}
                variant="outlined"
                size="medium"
                value={searchQuery}
                onChange={handleSearchInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <IconButton size="small" aria-label="search">
                        <SearchIcon />
                      </IconButton>
                      <Divider orientation="vertical" flexItem sx={{ ml: 1, width: '2px', height: 24, bgcolor: 'grey.300' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={handleFieldButtonClick}
                          aria-label="open search fields"
                          sx={{ width: 28, height: 28, p: 0 }}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  )
                }}
                sx={{ maxWidth: 180 }}
              />
            ) : (
              <TextField
                label={searchLabel}
                variant="outlined"
                size="medium"
                value={searchQuery}
                onChange={handleSearchInputChange}
                InputProps={{
                  endAdornment: (
                    <IconButton size="medium" aria-label="search">
                      <SearchIcon />
                    </IconButton>
                  )
                }}
                sx={{ mr: 1 }}
              />
            ))}

          <Menu anchorEl={fieldMenuAnchor} open={openFieldMenu} onClose={handleFieldMenuClose}>
            {Array.isArray(searchFieldOptions) &&
              searchFieldOptions.map((opt) => (
                <MenuItem
                  key={String(opt.value)}
                  selected={String(opt.value) === String(internalSearchField)}
                  onClick={() => handleFieldSelect(opt.value)}
                >
                  {opt.label}
                </MenuItem>
              ))}
          </Menu>

          {normalizedExtraButtons.length > 0 &&
            normalizedExtraButtons.map((btn, idx) => <React.Fragment key={`extra-btn-${idx}`}>{btn}</React.Fragment>)}

          {!hideAddButton && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddClick}>
              {t('general.add', 'Add')}
            </Button>
          )}

          {!hideExportButton && (
            <>
              <Button
                startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                onClick={handleExportClick}
                disabled={isExporting} // Disable tombol saat sedang download
                aria-controls={open ? 'export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                variant="outlined"
              >
                {isExporting ? t('general.exporting', 'Exporting...') : t('general.export', 'Export')}
              </Button>

              <Menu id="export-menu" anchorEl={anchorEl} open={open} onClose={handleExportClose}>
                <MenuItem onClick={exportToCSV}>{t('table.export.csv', 'Export to CSV')}</MenuItem>
                <MenuItem onClick={exportToExcel}>{t('table.export.excel', 'Export to Excel')}</MenuItem>
                <MenuItem onClick={exportToPDF}>{t('table.export.pdf', 'Export to PDF')}</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', width: '100%' }} ref={tableRef}>
        <DataGrid
          rows={rows || []}
          columns={dynamicColumns}
          columnVisibilityModel={React.useMemo(() => {
            const model = {};
            dynamicColumns.forEach((c) => {
              if (c.hide) model[c.field] = false;
            });
            return model;
          }, [dynamicColumns])}
          columnGroupingModel={columnGroupingModel}
          loading={!!loading}
          pageSizeOptions={pageSizeOptions}
          paginationMode={paginationMode}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          getRowId={(row) => row.id ?? row._id ?? JSON.stringify(row)}
          density="compact"
          localeText={activeLocaleText}
          rowHeight={rowHeight}
          columnHeaderHeight={headerHeight}
          onCellClick={onCellClick}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#5166AE !important',
              color: '#ffffff !important'
            },

            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#5166AE !important',
              color: '#ffffff !important'
            },
            '& .no-wrap-header .MuiDataGrid-columnHeaderTitle': { whiteSpace: 'nowrap' },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              whiteSpace: 'normal',
              lineHeight: '1.1',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'left'
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-cell': {
              borderColor: (theme) => theme.palette.divider
            },
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => theme.palette.divider,
            fontSize: 14,
            '& .MuiDataGrid-row': { cursor: cursorPointer ? 'pointer' : 'default' }
          }}
        />
      </Box>

      <SelectionMultiDialog
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        title={t('dataTable.selectedCompanies', 'Selected Companies')}
        options={(companyOptions || []).map((o) => ({ value: o.value, label: o.label ?? o.name ?? String(o.value) }))}
        initialSelected={selectedCompanyValues}
        onApply={(selectedArray) => {
          const selectedObjs = (companyOptions || [])
            .filter((opt) => selectedArray.includes(String(opt.value)))
            .map((opt) => ({ value: opt.value, label: opt.label ?? opt.name ?? String(opt.value) }));
          onCompanyChange(selectedObjs);
          setLocalSelectedCompanies(selectedObjs);
          setCompanyModalOpen(false);
        }}
        placeholder={t('dataTable.searchCompany', 'Search company...')}
      />

      <SelectionMultiDialog
        open={plateModalOpen}
        onClose={() => setPlateModalOpen(false)}
        title={t('dataTable.selectedPlates', 'Selected Plates')}
        options={(plateOptions || []).map((o) => ({
          value: o.value ?? o.id ?? o,
          label: o.label ?? o.name ?? String(o.value ?? o.id ?? o)
        }))}
        initialSelected={selectedPlateValues}
        onApply={(selectedArray) => {
          const selectedObjs = (plateOptions || [])
            .filter((opt) => selectedArray.includes(String(opt.value ?? opt.id ?? opt)))
            .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
          onPlateChange(selectedObjs);
          setLocalSelectedPlates(selectedObjs);
          setPlateModalOpen(false);
        }}
        placeholder={t('dataTable.searchPlate', 'Search plate...')}
      />

      <SelectionMultiDialog
        open={vehicleTypeModalOpen}
        onClose={() => setVehicleTypeModalOpen(false)}
        title={t('dataTable.selectedVehicleTypes', 'Selected Vehicle Types')}
        options={(vehicleTypeOptions || []).map((o) => ({
          value: o.value ?? o.id ?? o,
          label: o.label ?? o.name ?? String(o.value ?? o.id ?? o)
        }))}
        initialSelected={selectedVehicleTypeValues}
        onApply={(selectedArray) => {
          const selectedObjs = (vehicleTypeOptions || [])
            .filter((opt) => selectedArray.includes(String(opt.value ?? opt.id ?? opt)))
            .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
          onVehicleTypeChange(selectedObjs);
          setLocalSelectedVehicleTypes(selectedObjs);
          setVehicleTypeModalOpen(false);
        }}
        placeholder={t('dataTable.searchVehicleType', 'Search vehicle type...')}
      />

      <SelectionMultiDialog
        open={inspectedModalOpen}
        onClose={() => setInspectedModalOpen(false)}
        title={t('dataTable.selectedDayInspected', 'Selected Day Inspected')}
        options={(inspectedOptions || []).map((o) => ({
          value: o.value ?? o.id ?? o,
          label: o.label ?? o.name ?? String(o.value ?? o.id ?? o)
        }))}
        initialSelected={selectedInspectedValues}
        onApply={(selectedArray) => {
          const selectedObjs = (inspectedOptions || [])
            .filter((opt) => selectedArray.includes(String(opt.value ?? opt.id ?? opt)))
            .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
          onInspectedChange(selectedObjs);
          setLocalSelectedInspected(selectedObjs);
          setInspectedModalOpen(false);
        }}
        placeholder={t('dataTable.searchDayInspected', 'Search day inspected...')}
      />

      <SelectionMultiDialog
        open={notInspectedModalOpen}
        onClose={() => setNotInspectedModalOpen(false)}
        title={t('dataTable.selectedDayNotInspected', 'Selected Day Not Inspected')}
        options={(notInspectedOptions || []).map((o) => ({
          value: o.value ?? o.id ?? o,
          label: o.label ?? o.name ?? String(o.value ?? o.id ?? o)
        }))}
        initialSelected={selectedNotInspectedValues}
        onApply={(selectedArray) => {
          const selectedObjs = (notInspectedOptions || [])
            .filter((opt) => selectedArray.includes(String(opt.value ?? opt.id ?? opt)))
            .map((opt) => ({ value: opt.value ?? opt.id ?? opt, label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt) }));
          onNotInspectedChange(selectedObjs);
          setLocalSelectedNotInspected(selectedObjs);
          setNotInspectedModalOpen(false);
        }}
        placeholder={t('dataTable.searchDayNotInspected', 'Search day not inspected...')}
      />
    </Paper>
  );
};

export default DataTable;
